import { createSelector } from "reselect";
import { get, reduceRight } from 'lodash';

import green from "@material-ui/core/colors/green";
import grey from "@material-ui/core/colors/grey";
import { getLastPmax } from "../reducers/chart_data_reducer";

const chartDataSelector = state => state.chartData;
const layoutSelector = state => state.chartData.layout;
const assetSelector = state => state.asset;
const isAtypicalSelector = state => state.config.config.ATYPICAL;
const pMaxOnPeak = state => state.siteData.pMaxOnPeak;
const pMaxOffPeak = state => state.siteData.pMaxOffPeak;
const batteryBams = state => state.chartData.data.batteryBams;

const dataGeneric = [{
  type: "scatter",
  mode: "lines+markers",
  name: "Pmax",
  x: [],
  y: [],
  line: { color: "rgba(255, 0, 0, .4)" },
  visible: false
}];

const dataAtypical = [
  {
    type: 'bar',
    x: [],
    y: [],
    width: []
  },
  {
    type: "scatter",
    mode: "lines+markers",
    name: "Max on peak",
    x: [],
    y: [],
    line: { color: "green" },
    visible: false
  },
  {
    type: "scatter",
    mode: "lines+markers",
    name: "Max off peak",
    x: [],
    y: [],
    line: { color: "purple" },
    visible: false
  }
];

const dataCommon = [
  {
    type: "scatter",
    mode: "lines",
    name: "Load",
    x: [],
    y: [],
    line: {
      color: "#001A70",
      shape: 'hv'
    },
  },
  {
    type: "scatter",
    mode: "lines",
    name: "Load Without BESS",
    x: [],
    y: [],
    line: {
      color: "#005BBB",
      dash: "dot",
      shape: 'hv'
    }
  },
  {
    type: "scatter",
    mode: "lines",
    name: "Battery<br>-&nbsp;charge<br>+&nbsp;discharge",
    hovertemplate: '%{y}kW<extra>Battery</extra>',
    x: [],
    y: [],
    line: { color: green[500] }
  }
];

const layout = {
  uirevision: "same",
  autosize: true,
  displaylogo: false,
  // showlegend: false,
  margin: {
    l: 50,
    r: 30,
    t: 10,
    b: 40
  },
  // legend: { orientation: "h" },
  xaxis: {
    autorange: true,
    //range: [0, 1],
    rangeselector: {
      buttons: [
        {
          count: 1,
          label: "1d",
          step: "day",
          stepmode: "backward"
        },
        {
          count: 6,
          label: "6h",
          step: "hour",
          stepmode: "backward"
        },
        {
          count: 1,
          label: "1h",
          step: "hour",
          stepmode: "backward"
        },
        { step: "all" }
      ]
    },
    //rangeslider: { range: [rangeStart, rangeEnd] },
    type: "date"
  },
  yaxis: {
    title: "kW",
    autorange: true,
    type: "linear",
    hoverformat: ".0f"
  },
  yaxis2: {
    autorange: false,
    overlaying: "y",
    side: "right",
    hoverformat: ".2r"
  },
  annotations: [
    {
      xref: "paper",
      x: 0,
      y: 581,
      xanchor: "left",
      yanchor: "bottom",
      text: "Pref",// or pMaxOffPeak
      showarrow: false
    },
    {
      xref: "paper",
      x: 0,
      y: 424,
      xanchor: "left",
      yanchor: "bottom",
      text: "Pmax",
      showarrow: false,
      visible: false
    }
  ],
  shapes: [
    {
      type: "line",
      y0: 0,
      y1: 0,
      x0: 0,
      x1: 0,
      line: {
        color: grey[500],
        width: 2,
        dash: "dashdot"
      }
    },
    {
      type: "line",
      y0: 0,
      y1: 0,
      x0: 0,
      x1: 0,
      line: {
        color: "rgba(255, 0, 0, .4)",
        width: 2,
        dash: "dashdot"
      },
      visible: false
    }
  ]
};

const reshapeData = (
  chartData,
  layoutData,
  assetData,
  isAtypical,
  pMax,
  lastPmaxOffPeak,
  lastPmaxOnPeak,
  batteryBams
) => {
  const initialData = {
    data: (isAtypical ? dataAtypical : dataGeneric).concat(dataCommon),
    layout
  };
  const { data } = initialData;

  const startDate = chartData.data.battery[0].timestamp;
  const endDate = chartData.data.battery[chartData.data.battery.length - 1].timestamp;

  const { p_ref_kw } = assetData.peak_shaving_generic;

  const pRef = p_ref_kw;
  // const pMax = isAtypical ? p_max_on_peak_kw : pMax;

  const columnIndexes = isAtypical ? {
    tariff: 0,
    pMaxOnPeak: 1,
    pMaxOffPeak: 2,
    meterWithBess: 3,
    meterWithoutBess: 4,
    battery: 5
  } : {
    pMax: 0,
    meterWithBess: 1,
    meterWithoutBess: 2,
    battery: 3
  };

  let reshapedData = { ...initialData };
  reshapedData.layout = { ...layout };
  reshapedData.data = [...data];


  const { battery, pMax: pMaxValues, pMaxOnPeak, pMaxOffPeak, meter_with_bess, meter_without_bess } = chartData.data;

  //update battery data
  try {
    reshapedData.data[columnIndexes.battery].x = battery.reduce((acc, datapoint) => {
      acc.push(datapoint.timestamp);
      return acc;
    }, []);

    reshapedData.data[columnIndexes.battery].y = battery.reduce((acc, datapoint) => {
      acc.push(datapoint.p);
      return acc;
    }, []);
  } catch (error) {
    console.log("error", error);
  }

  if (!isAtypical) {
    //update Pmax data
    try {
      reshapedData.data[columnIndexes.pMax].x = pMaxValues.reduce((acc, datapoint) => {
        acc.push(datapoint.timestamp);
        return acc;
      }, []);

      reshapedData.data[columnIndexes.pMax].y = pMaxValues.reduce((acc, datapoint) => {
        acc.push(datapoint.value);
        return acc;
      }, []);
    } catch (error) {
      console.log("error", error);
    }
  }

  if (isAtypical) {
    //update pMaxOnPeak data
    try {
      reshapedData.data[columnIndexes.pMaxOnPeak].x = pMaxOnPeak.reduce((acc, datapoint) => {
        acc.push(datapoint.timestamp);
        return acc;
      }, []);

      reshapedData.data[columnIndexes.pMaxOnPeak].y = pMaxOnPeak.reduce((acc, datapoint) => {
        acc.push(datapoint.value);
        return acc;
      }, []);
    } catch (error) {
      console.log("error", error);
    }

    //update pMaxOffPeak data
    try {
      reshapedData.data[columnIndexes.pMaxOffPeak].x = pMaxOffPeak.reduce((acc, datapoint) => {
        acc.push(datapoint.timestamp);
        return acc;
      }, []);

      reshapedData.data[columnIndexes.pMaxOffPeak].y = pMaxOffPeak.reduce((acc, datapoint) => {
        acc.push(datapoint.value);
        return acc;
      }, []);
    } catch (error) {
      console.log("error", error);
    }
  }

  const showGreyLine = value => {
    reshapedData.layout.annotations[0].y = value;
    reshapedData.layout.shapes[0].x0 = startDate;
    reshapedData.layout.shapes[0].x1 = endDate;
    reshapedData.layout.shapes[0].y0 = value;
    reshapedData.layout.shapes[0].y1 = value;
  };

  const showRedLine = value => {
    reshapedData.layout.annotations[1].visible = true;
    reshapedData.layout.annotations[1].y = value;
    reshapedData.layout.shapes[1].visible = true;
    reshapedData.layout.shapes[1].x0 = startDate;
    reshapedData.layout.shapes[1].x1 = endDate;
    reshapedData.layout.shapes[1].y0 = value;
    reshapedData.layout.shapes[1].y1 = value;
  };

  // update layout data
  try {
    reshapedData.layout.shapes = [...layout.shapes];
    //reshapedData.layout.xaxis.range = [startDate, endDate];
    reshapedData.layout.annotations[0].text = isAtypical ? 'Pmax off peak' : reshapedData.layout.annotations[0].text;
    reshapedData.layout.annotations[1].text = isAtypical ? 'Pmax on peak' : reshapedData.layout.annotations[1].text;

    const greyValue = isAtypical ? lastPmaxOffPeak : pRef;
    if (greyValue !== null) {
      showGreyLine(isAtypical ? lastPmaxOffPeak : pRef);
    }

    if ((!isAtypical && pMax) || (isAtypical && (lastPmaxOnPeak !== null))) {
      showRedLine(isAtypical ? lastPmaxOnPeak : pMax);
    } else {
      reshapedData.layout.annotations[1].visible = false;
      reshapedData.layout.shapes[1].visible = false;
    }

    // Show tariff grey zones in background
    const selectedTariff = layoutData.tariff[0];

    const onPeakColor = "#e3e3e3";
    const offPeakColor = "#f0f0f0";
    const peakOpacity = 1;

    let showOnPeakLegend = false;
    let showOffPeakLegend = false;

    const findLastYDefinedPmax = (pMaxCollection, index2) => {
      const y = get(pMaxCollection.y, `[${index2}]`, 0);
      return y !== null ? y : findLastYDefinedPmax(pMaxCollection, index2 - 1);
    };

    isAtypical &&
    selectedTariff &&
    selectedTariff.values.forEach(
      (datapoint, index) => {

        const tariffStartTimestamp = datapoint.timestamp;
        const tariffEndTimestamp = get(selectedTariff.values, `[${index + 1}].timestamp`, endDate);

        // TODO remove it - debug purpose only
        /*reshapedData.layout.shapes.push({
          type: "rect",
          layer: "below",
          // x-reference is assigned to the x-values
          xref: "x",
          // y-reference is assigned to the plot paper [0,1]
          yref: "y",
          x0: tariffStartTimestamp,
          y0: 0,
          x1: tariffEndTimestamp,
          y1: 1500,
          fillcolor: datapoint.label === 'on-peak' ? 'red' : 'blue',
          opacity: 0.1,// 0.3
          line: {
            width: 0
          }
        });*/

        const pMaxCollection = reshapedData.data[columnIndexes[datapoint.label === 'on-peak' ? 'pMaxOnPeak' : 'pMaxOffPeak']];

        const pMaxs = reduceRight(
          pMaxCollection.x,
          (result, pointTimestamp, index2) => {
            // console.log('pointTimestamp <= tariffEndTimestamp', pointTimestamp <= tariffEndTimestamp);

            if (!result.last && pointTimestamp <= tariffEndTimestamp) {

              const point = {
                x: pointTimestamp,
                y: findLastYDefinedPmax(pMaxCollection, index2),
                label: datapoint.label
              };

              if (pointTimestamp <= tariffStartTimestamp) {
                result.last = true;
                point.x = tariffStartTimestamp;
              } else if (!result.points.length) {
                point.x = tariffEndTimestamp;
              }

              result.points.unshift(point);
            }
            return result;
          },
          {
            last: false,
            points: []
          }
        ).points;

        // If only one point found in list
        if (pMaxs.length === 1) {
          pMaxs.unshift({
            ...pMaxs[0],
            x: tariffStartTimestamp
          })
        }

        pMaxs.forEach((pMax, index3) => {

          const pMaxStartTimestamp = pMax.x;
          const pMaxEndTimestamp = get(pMaxs, `[${index3 + 1}].x`, tariffEndTimestamp);
          const isOnPeak = datapoint.label === 'on-peak';

          if (pMax.y) {
            showOnPeakLegend = showOnPeakLegend || isOnPeak;
            showOffPeakLegend = showOffPeakLegend || !isOnPeak;

            reshapedData.layout.shapes.push({
              type: "rect",
              layer: "below",
              // x-reference is assigned to the x-values
              xref: "x",
              // y-reference is assigned to the plot paper [0,1]
              yref: "y",
              x0: pMaxStartTimestamp - 0,
              y0: 0,
              x1: pMaxEndTimestamp,
              y1: pMax.y,
              fillcolor: isOnPeak ? onPeakColor : offPeakColor,
              opacity: peakOpacity,// 0.3
              line: {
                width: 0
              }
            });
          }
        });
      }
    );

    // On peak off peak fake legends
    const peakShapeConfig = {
      type: "scatter",
      mode: "lines",
      x: [endDate],
      y: 0,
      line: { width: 0 },
      fill: 'tozeroy',
      visible: true,
      hoverinfo: 'none'
    };

    if (showOnPeakLegend) {
      reshapedData.data.push({
        ...peakShapeConfig,
        name: "Pmax on peak",
        fillcolor: onPeakColor
      });
    }

    if (showOffPeakLegend) {
      reshapedData.data.push({
        ...peakShapeConfig,
        name: "Pmax off peak",
        fillcolor: offPeakColor
      });
    }

  } catch (error) {
    console.log("error", error);
  }

  // update meter with bess data
  try {
    reshapedData.data[columnIndexes.meterWithBess].x = meter_with_bess.reduce((acc, datapoint) => {
      acc.push(datapoint.timestamp);
      return acc;
    }, []);

    reshapedData.data[columnIndexes.meterWithBess].y = meter_with_bess.reduce((acc, datapoint) => {
      acc.push(datapoint.p);
      return acc;
    }, []);
  } catch (error) {
    console.log("error", error);
  }

  // update meter without bess data
  try {
    reshapedData.data[columnIndexes.meterWithoutBess].x = meter_without_bess.reduce((acc, datapoint) => {
      acc.push(datapoint.timestamp);
      return acc;
    }, []);

    reshapedData.data[columnIndexes.meterWithoutBess].y = meter_without_bess.reduce((acc, datapoint) => {
      acc.push(datapoint.p);
      return acc;
    }, []);
  } catch (error) {
    console.log("error", error);
  }
  reshapedData.layout.xaxis.autorange = true;
  reshapedData.layout.yaxis.autorange = true;
  reshapedData.layout.yaxis2.autorange = false;

  // Add Battery BAMS values
  if (batteryBams.length) {

    const color = 'rgb(0,118,79)';

    const trace = {
      x: batteryBams.map(({ timestamp }) => timestamp),
      y: batteryBams.map(({ soc }) => soc),
      name: 'SOC',
      type: 'scatter',
      yaxis: 'y2',
      line: { color },
      hovertemplate: '%{y}%<extra>SOC</extra>'
    };

    reshapedData.data.splice(6, 0, trace);

    reshapedData.layout.yaxis2 = {
      titlefont: { color },
      tickfont: { color },
      overlaying: 'y',
      side: 'right',
      range: [0, 300],
      autorange: false,
      tickvals: [25, 50, 75, 100],
      ticktext: [25, 50, 75, 100]
    };
  }

  return { ...reshapedData };
};

export default createSelector(
  chartDataSelector,
  layoutSelector,
  assetSelector,
  isAtypicalSelector,
  getLastPmax,
  pMaxOffPeak,
  pMaxOnPeak,
  batteryBams,
  reshapeData
);



// WEBPACK FOOTER //
// ./src/selectors/reshape_data_for_chart.js